import {action, makeObservable, observable} from 'mobx';
import {Form} from './Form';
import {SavedSearch} from '../../features/Form/types';
import {PatchSavedSearch} from '../../features/Form/components/AdvancedSearch/AdvancedSearchDrawer';
import {
    createNewSearch,
    patchSavedSearch
} from '../../features/Form/components/AdvancedSearch/AdvancedSearchDrawer/utils';
import {findAvailableIndex} from '../../features/Form/actions/utils/filter';
import {createSavedSearch} from '../../features/Form/actions/searches';
import {texts} from '../../features/Form/components/AdvancedSearch/AdvancedSearchDrawer/texts';
import {lockScreenKeyboardNavigation} from '../../features/Form/actions/lockScreenKeyboardNavigation';

export class SearchStore {
    form: Form;
    defaultAIAssistant: boolean = false;

    @observable recentSearches: SavedSearch[] = [];
    @observable savedSearches: SavedSearch[] = [];
    @observable isAdvancedSearchOpen: boolean = false;
    @observable currentSearchId: string = null; // ID of the current applied search (i.e. one from saved searches)
    @observable editSearch: PatchSavedSearch = null; // Search that is being edited in the advanced search drawer

    constructor(form: Form) {
        makeObservable(this);
        this.form = form;
    }

    @action setCurrentSearchId = (id: string) => (this.currentSearchId = id);

    @action setRecentSearches = (searches: SavedSearch[]) => (this.recentSearches = searches);

    @action setFormSavedSearches = (searches: SavedSearch[]) => {
        this.savedSearches = searches.sort((a, b) => a.index - b.index);
    };

    @action toggleAdvancedSearch = (flag: boolean) => {
        this.isAdvancedSearchOpen = flag;

        if (flag) {
            lockScreenKeyboardNavigation(true);
        } else {
            lockScreenKeyboardNavigation(false);
            this.editSearch = null;
            this.defaultAIAssistant = false;
        }
    };

    openNewSearch = (defaultAIAssistant?: boolean) => {
        this.toggleAdvancedSearch(true);
        this.editSearch = this.createEmptySearch();
        this.defaultAIAssistant = defaultAIAssistant;
    };

    openSearch = (id: string | null = null) => {
        const searchID = id || this.currentSearchId;
        let search: PatchSavedSearch = null;

        if (searchID) {
            /* If the  searchID is not provided, we open the current active search (i.e. one from saved searches) */
            const savedSearch = this.savedSearches.find(search => search.id === searchID);
            if (savedSearch) {
                search = patchSavedSearch(savedSearch, this.form);
            }
        }

        if (!search) {
            /* If the search is not found, we create a new one */
            search = patchSavedSearch(createSavedSearch(texts.newSearch, this.form), this.form);
        }

        this.editSearch = search;

        this.toggleAdvancedSearch(true);
    };

    createEmptySearch = (): PatchSavedSearch => {
        return createNewSearch(null, findAvailableIndex(this.savedSearches));
    };

    setDefaultSearch = (id: string): SavedSearch[] => {
        const updatedSearches = this.savedSearches.map(search => ({
            ...search,
            isDefault: search.id === id ? !search.isDefault : false
        }));

        this.setFormSavedSearches(updatedSearches);
        return updatedSearches;
    };
}
